import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import { useTranslation } from 'react-i18next';
import { GROUPS, STATES, normalizeVersatility, hasAnyValue } from '../../utils/versatility';
import GraduatedScale from './GraduatedScale';

/**
 * Read-only visualization of a player's defensive versatility profile.
 * 3 groups (GUARD/WING/BIG) × 4 cells, three explicit color states + an untouched default.
 */
const DefensiveVersatilityChart = ({
  value,
  compact = false,
  showLegend,
  showGroups,
  showTitle = false,
  emptyHint = true
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const v = normalizeVersatility(value);
  const empty = !hasAnyValue(v);

  const _showLegend = showLegend === undefined ? !compact : showLegend;
  const _showGroups = showGroups === undefined ? !compact : showGroups;

  const cellSize = compact ? 16 : 26;
  const cellGap = compact ? 2 : 3;
  const groupGap = compact ? 6 : 10;
  const labelFs = compact ? '0.6rem' : '0.7rem';

  const colors = {
    [STATES.PRIMARY]: theme.palette.success.main,
    [STATES.SWITCH]: '#fbc02d',
    [STATES.CANT]: theme.palette.error.main,
    none: alpha(theme.palette.grey[400], 0.18)
  };

  const cellColor = (state) => {
    if (state === STATES.PRIMARY) return colors[STATES.PRIMARY];
    if (state === STATES.SWITCH) return colors[STATES.SWITCH];
    if (state === STATES.CANT) return colors[STATES.CANT];
    return colors.none;
  };

  if (empty && emptyHint && !compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        <ShieldIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption">{t('defensiveVersatility.empty')}</Typography>
      </Box>
    );
  }

  if (empty && compact) return null;

  const groupCellsWidth = 4 * cellSize + 3 * cellGap;

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: compact ? 0.5 : 1 }}>
      {showTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ShieldIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {t('defensiveVersatility.title')}
          </Typography>
        </Box>
      )}

      {/* Group bands */}
      {_showGroups && (
        <Box sx={{ display: 'flex', gap: `${groupGap}px` }}>
          {GROUPS.map(group => (
            <Box
              key={group.key}
              sx={{
                width: `${groupCellsWidth}px`,
                py: compact ? 0.3 : 0.5,
                borderRadius: 0.75,
                backgroundColor: alpha(theme.palette.text.primary, 0.85),
                color: theme.palette.background.paper,
                textAlign: 'center',
                boxSizing: 'border-box'
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontSize: labelFs, fontWeight: 700, letterSpacing: 0.4, lineHeight: 1 }}
              >
                {t(group.labelKey)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Numeric ruler (skip in compact mode) */}
      {!compact && (
        <GraduatedScale cellSize={cellSize} cellGap={cellGap} groupGap={groupGap} />
      )}

      {/* Cells */}
      <Box sx={{ display: 'flex', gap: `${groupGap}px` }}>
        {GROUPS.map(group => (
          <Box key={group.key} sx={{ display: 'flex', gap: `${cellGap}px` }}>
            {group.positions.map(p => {
              const state = v[p];
              return (
                <Box
                  key={p}
                  sx={{
                    width: cellSize,
                    height: cellSize,
                    borderRadius: 0.5,
                    backgroundColor: cellColor(state),
                    border: state ? 'none' : `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    boxSizing: 'border-box',
                    transition: 'background-color 0.15s ease'
                  }}
                />
              );
            })}
          </Box>
        ))}
      </Box>

      {_showLegend && (
        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[STATES.PRIMARY] }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {t('defensiveVersatility.legend.primary')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[STATES.SWITCH] }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {t('defensiveVersatility.legend.switch')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[STATES.CANT] }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {t('defensiveVersatility.legend.cant')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DefensiveVersatilityChart;
